<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Knappar
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Tre huvudvarianter: primär (fylld teal), sekundär (kantad teal) och ghost
      (transparent). Hovra eller tabba för att se tillstånden.
    </p>

    <PreviewBox label="Varianter — hovra för att se hover-tillstånd">
      <button
        class="flex items-center gap-2 px-6 py-3 rounded-xl border-none cursor-pointer font-[Poppins] font-bold text-sm text-white transition-colors duration-150"
        :style="{ background: hovered === 'primary' ? '#0f9a8c' : '#14b8a6' }"
        @mouseenter="hovered = 'primary'"
        @mouseleave="hovered = null"
      >
        Primär knapp
      </button>
      <button
        class="flex items-center gap-2 px-6 py-3 rounded-xl cursor-pointer font-[Poppins] font-bold text-sm transition-colors duration-150"
        :style="{
          background: hovered === 'secondary' ? '#f0fdfc' : 'white',
          color: '#14b8a6',
          border: '1.5px solid #14b8a6',
        }"
        @mouseenter="hovered = 'secondary'"
        @mouseleave="hovered = null"
      >
        Sekundär knapp
      </button>
      <button
        class="flex items-center gap-2 px-6 py-3 rounded-xl cursor-pointer font-[Poppins] font-bold text-sm text-[#191c1e] transition-colors duration-150"
        :style="{
          background: hovered === 'ghost' ? '#f2f4f6' : 'transparent',
          border: '1.5px solid #eceef0',
        }"
        @mouseenter="hovered = 'ghost'"
        @mouseleave="hovered = null"
      >
        Ghost knapp
      </button>
      <button
        class="flex items-center gap-2 px-6 py-3 rounded-xl border-none cursor-pointer font-[Poppins] font-bold text-sm text-white transition-colors duration-150"
        :style="{ background: hovered === 'icon' ? '#0f9a8c' : '#14b8a6' }"
        @mouseenter="hovered = 'icon'"
        @mouseleave="hovered = null"
      >
        Med ikon <ArrowRight :size="16" />
      </button>
      <button
        class="flex items-center justify-center w-11 h-11 rounded-full cursor-pointer transition-colors duration-150"
        :style="{
          background: hovered === 'circle' ? '#f2f4f6' : 'white',
          border: '1.5px solid #eceef0',
        }"
        @mouseenter="hovered = 'circle'"
        @mouseleave="hovered = null"
      >
        <Heart :size="18" color="#3c4947" />
      </button>
    </PreviewBox>

    <!-- State grid -->
    <PreviewBox label="Tillstånd sida vid sida">
      <div class="flex flex-col gap-3 w-full">
        <div class="grid gap-3" style="grid-template-columns: 80px 1fr 1fr 1fr">
          <span
            class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[0.8px] uppercase self-center"
            >Variant</span
          >
          <span
            class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[0.8px] uppercase text-center"
            >Normal</span
          >
          <span
            class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[0.8px] uppercase text-center"
            >Hover</span
          >
          <span
            class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[0.8px] uppercase text-center"
            >Focus</span
          >
        </div>
        <!-- Primary -->
        <div
          class="grid gap-3 items-center"
          style="grid-template-columns: 80px 1fr 1fr 1fr"
        >
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >Primär</span
          >
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl bg-teal-500 border-none font-[Poppins] font-bold text-sm text-white"
            >
              Normal
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl border-none font-[Poppins] font-bold text-sm text-white"
              style="background: #0f9a8c"
            >
              Hover
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl border-none font-[Poppins] font-bold text-sm text-white outline-none ring-2 ring-offset-2"
              style="background: #14b8a6; ring-color: #14b8a6"
            >
              Focus
            </button>
          </div>
        </div>
        <!-- Secondary -->
        <div
          class="grid gap-3 items-center"
          style="grid-template-columns: 80px 1fr 1fr 1fr"
        >
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >Sekundär</span
          >
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl bg-white font-[Poppins] font-bold text-sm text-teal-500"
              style="border: 1.5px solid #14b8a6"
            >
              Normal
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl font-[Poppins] font-bold text-sm text-teal-500"
              style="background: #f0fdfc; border: 1.5px solid #14b8a6"
            >
              Hover
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl font-[Poppins] font-bold text-sm text-teal-500 ring-2 ring-offset-2 ring-teal-500 outline-none"
              style="background: white; border: 1.5px solid #14b8a6"
            >
              Focus
            </button>
          </div>
        </div>
        <!-- Ghost -->
        <div
          class="grid gap-3 items-center"
          style="grid-template-columns: 80px 1fr 1fr 1fr"
        >
          <span class="font-[Plus_Jakarta_Sans] text-[13px] text-[#6c7a77]"
            >Ghost</span
          >
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl bg-transparent font-[Poppins] font-bold text-sm text-[#191c1e]"
              style="border: 1.5px solid #eceef0"
            >
              Normal
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl font-[Poppins] font-bold text-sm text-[#191c1e]"
              style="background: #f2f4f6; border: 1.5px solid #eceef0"
            >
              Hover
            </button>
          </div>
          <div class="flex justify-center">
            <button
              class="px-4 py-2 rounded-xl font-[Poppins] font-bold text-sm text-[#191c1e] ring-2 ring-offset-2 ring-[#191c1e] outline-none"
              style="background: transparent; border: 1.5px solid #eceef0"
            >
              Focus
            </button>
          </div>
        </div>
      </div>
    </PreviewBox>

    <PreviewBox label="Storlekar" bg="white">
      <button
        class="px-7 py-3.5 rounded-xl bg-teal-500 border-none cursor-pointer font-[Poppins] font-bold text-base text-white"
      >
        Large
      </button>
      <button
        class="px-5 py-2.5 rounded-xl bg-teal-500 border-none cursor-pointer font-[Poppins] font-bold text-sm text-white"
      >
        Medium
      </button>
      <button
        class="px-4 py-1.5 rounded-xl bg-teal-500 border-none cursor-pointer font-[Poppins] font-bold text-xs text-white"
      >
        Small
      </button>
    </PreviewBox>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Heart, ArrowRight } from "lucide-vue-next";
import PreviewBox from "../components/PreviewBox.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlCode = `<!-- Primär -->
<button class="px-6 py-3 rounded-xl bg-teal-500 border-none font-[Poppins] font-bold text-sm text-white hover:bg-teal-600 transition-colors">
  Primär knapp
</button>

<!-- Sekundär -->
<button class="px-6 py-3 rounded-xl bg-white font-[Poppins] font-bold text-sm text-teal-500 transition-colors hover:bg-teal-50" style="border: 1.5px solid #14b8a6">
  Sekundär knapp
</button>

<!-- Ghost -->
<button class="px-6 py-3 rounded-xl bg-transparent font-[Poppins] font-bold text-sm text-[#191c1e] transition-colors hover:bg-[#f2f4f6]" style="border: 1.5px solid #eceef0">
  Ghost knapp
</button>

<!-- Med ikon -->
<button class="flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-500 border-none font-[Poppins] font-bold text-sm text-white">
  Med ikon <ArrowRight :size="16" />
</button>`;
const cssCode = `/* Primär knapp */
border-radius: 12px;           /* rounded-xl */
background: #14b8a6;           /* bg-teal-500 */
font-family: 'Poppins', sans-serif;
font-weight: 700;

/* Ikonknappar */
border-radius: 9999px;         /* rounded-full */

/* Hover */
/* Primär:  */ background: #0f9a8c;  /* teal-600 */
/* Sekundär: */ background: #f0fdfc;  /* teal-50 */
/* Ghost:    */ background: #f2f4f6;

/* Focus */
outline: 2px solid currentColor;  /* ring-2 */
outline-offset: 2px;              /* ring-offset-2 */`;

const snippetTabs = [
  { label: "HTML", code: htmlCode },
  { label: "CSS", code: cssCode, lang: "css" },
];
const hovered = ref(null);
</script>
